import { useEffect, useRef, useState } from 'react';
import { Alert, Badge, Button, Card, Form, Spinner } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import HelpSearch from './HelpSearch';

const PARTNER_STARTERS = [
  'Where do I find the Tally export, and who can see it?',
  'How do I bring a customer\'s opening balances in from Tally?',
  'What can a Finance Manager do that a Finance Operator cannot?'
];
const CUSTOMER_STARTERS = [
  'How do I export my sales to Tally?',
  'How do I record a payment to a vendor?',
  'Why can\'t I see a menu that my colleague sees?'
];

/** The answer as paragraphs, "-" and numbered lists and **bold**, with no HTML from the answer ever inserted into the page. */
function AnswerText({ text }) {
  const bold = (line) => line.split(/(\*\*[^*]+\*\*)/g).map((part, i) => (
    part.startsWith('**') && part.endsWith('**') ? <strong key={i}>{part.slice(2, -2)}</strong> : part
  ));
  const blocks = [];
  let list = null;
  text.split('\n').forEach((raw) => {
    const line = raw.trim();
    const item = line.match(/^(?:[-*]|\d+[.)])\s+(.*)$/);
    if (item) {
      const ordered = /^\d/.test(line);
      if (!list || list.ordered !== ordered) {
        list = { ordered, items: [] };
        blocks.push(list);
      }
      list.items.push(item[1]);
    } else if (line) {
      list = null;
      blocks.push({ paragraph: line });
    } else {
      list = null;
    }
  });
  return (
    <>
      {blocks.map((b, i) => {
        if (b.paragraph) return <p key={i} className="mb-2">{bold(b.paragraph)}</p>;
        const Tag = b.ordered ? 'ol' : 'ul';
        return <Tag key={i} className="mb-2">{b.items.map((it, j) => <li key={j}>{bold(it)}</li>)}</Tag>;
      })}
    </>
  );
}

/**
 * The help search with the assistant under it, used in two places: Account Management > Search & Ask for account managers
 * (partner, which also finds their own topics) and Help & Support (an account manager gets the partner version there too, so both
 * places behave the same; everyone else gets the customers' version). embedded: shown inside the Help & Support page, so no page
 * margins and no notes about where else to look. An assistant that answers from iBigDo's own
 * help text and says when the help does not have the answer; a question can be sent on to support from here. Questions are
 * kept for System Admin to see what the help is missing. Where the assistant is off, or cannot be reached, only the search
 * shows (account managers also get a note saying so).
 */
function HelpAssistant({ apiBase = '/api/account-manager/assistant', partner = true, embedded = false }) {
  const [status, setStatus] = useState(null);
  const [messages, setMessages] = useState([]); // { role, content, id, covered, helpful, sent }
  const [question, setQuestion] = useState('');
  const [busy, setBusy] = useState(false);
  const bottom = useRef(null);
  const askBox = useRef(null);

  useEffect(() => {
    axios.get(`${apiBase}/status`)
      .then((r) => setStatus(r.data))
      .catch(() => setStatus({ enabled: false }));
  }, [apiBase]);

  useEffect(() => {
    if (bottom.current && bottom.current.scrollIntoView) bottom.current.scrollIntoView({ block: 'nearest' });
  }, [messages, busy]);

  const ask = async (text) => {
    const q = (text || question).trim();
    if (!q || busy) return;
    // A follow-up names the answer it follows; the server rebuilds the conversation from what it really said
    const lastAnswer = [...messages].reverse().find((m) => m.role === 'assistant' && m.id);
    setMessages((prev) => [...prev, { role: 'user', content: q }]);
    setQuestion('');
    setBusy(true);
    try {
      const { data } = await axios.post(`${apiBase}/ask`, { question: q, previousId: lastAnswer ? lastAnswer.id : null });
      setMessages((prev) => [...prev, { role: 'assistant', content: data.answer, id: data.id, covered: data.covered, refused: data.refused }]);
      setStatus((s) => ({ ...s, usedToday: (s?.dailyLimit || 0) - data.remainingToday }));
    } catch (error) {
      const msg = error.response?.data;
      toast.error(typeof msg === 'string' && msg ? msg : 'The assistant could not answer just now');
      // Give the question back to the box so it is not lost
      setMessages((prev) => prev.slice(0, -1));
      setQuestion(q);
    } finally {
      setBusy(false);
    }
  };

  const rate = async (index, helpful) => {
    const m = messages[index];
    setMessages((prev) => prev.map((x, i) => (i === index ? { ...x, helpful } : x)));
    try {
      await axios.post(`${apiBase}/${m.id}/helpful`, { helpful });
    } catch (error) {
      // The verdict is a nicety; nothing to tell the person
    }
  };

  const sendToSupport = async (index) => {
    const answer = messages[index];
    const asked = messages[index - 1];
    if (answer.sending || answer.sent) return;
    setMessages((prev) => prev.map((x, i) => (i === index ? { ...x, sending: true } : x)));
    try {
      await axios.post('/api/feedback', {
        category: 'OTHER',
        message: `Question from the help assistant:\n\n${asked ? asked.content : ''}\n\nWhat the assistant answered:\n\n${answer.content}`
      });
      setMessages((prev) => prev.map((x, i) => (i === index ? { ...x, sending: false, sent: true } : x)));
      toast.success('Sent to support. Replies appear under Help & Support, My Requests.');
    } catch (error) {
      setMessages((prev) => prev.map((x, i) => (i === index ? { ...x, sending: false } : x)));
      toast.error('Could not send it to support. Please try again.');
    }
  };

  const wrapperClass = embedded ? '' : 'mt-3';
  const wrapperStyle = embedded ? undefined : { maxWidth: 820 };
  if (!status) {
    // Customers still get the search while this loads, or if the assistant cannot be reached
    return embedded
      ? <HelpSearch partner={partner} />
      : <div className="d-flex justify-content-center py-5"><Spinner animation="border" role="status" /></div>;
  }
  if (status.paused) {
    return (
      <div className={wrapperClass} style={wrapperStyle}>
        {embedded && <HelpSearch partner={partner} />}
        <Alert variant="warning" className={embedded ? 'mb-4' : 'mt-3'}>
          The assistant is paused for you for a day, because several of your questions were not about using
          {partner ? ' or selling' : ''} iBigDo, or were not respectful. It opens again by itself. If this is a mistake, please raise
          it with support from Help &amp; Support.
        </Alert>
      </div>
    );
  }
  if (!status.enabled) {
    if (embedded) return <HelpSearch partner={partner} />;
    return (
      <div className="mt-3" style={{ maxWidth: 820 }}>
        <HelpSearch partner />
        <Alert variant="info">
          The assistant is not switched on yet. Search the help above, or raise a question from Help &amp; Support (the lifebuoy at
          the top right) and the team will reply.
        </Alert>
      </div>
    );
  }

  const left = Math.max(0, (status.dailyLimit || 0) - (status.usedToday || 0));

  return (
    <div className={wrapperClass} style={wrapperStyle}>
      <HelpSearch partner={partner} onAsk={(words) => { setQuestion(words); if (askBox.current) askBox.current.focus(); }} />

      <h5 className="mt-4"><i className="bi bi-chat-dots me-2" />Not found? Ask the assistant</h5>
      <p className="text-muted">
        Ask how something works in iBigDo. Answers come from iBigDo&apos;s own help, and the assistant says when the help does not
        cover a question.{' '}
        {partner
          ? 'It does not see any customer\'s data.'
          : 'It cannot see your own data, such as your bills or stock, and cannot change anything for you. Please do not type customer names, phone numbers or other business details.'}
        {' '}Please search above first: it is quicker, and the number of questions a day is limited.
      </p>

      {messages.length === 0 && (
        <div className="mb-3">
          <div className="small text-muted mb-1">Try asking</div>
          <div className="d-flex flex-wrap gap-2">
            {(partner ? PARTNER_STARTERS : CUSTOMER_STARTERS).map((s) => (
              <Button key={s} size="sm" variant="outline-secondary" onClick={() => ask(s)}>{s}</Button>
            ))}
          </div>
        </div>
      )}

      {messages.map((m, i) => (
        m.role === 'user' ? (
          <div key={i} className="d-flex justify-content-end mb-2">
            <div className="bg-primary text-white rounded px-3 py-2" style={{ maxWidth: '85%', whiteSpace: 'pre-wrap' }}>{m.content}</div>
          </div>
        ) : (
          <Card key={i} className="mb-3">
            <Card.Body className="py-2">
              <AnswerText text={m.content} />
              {!m.refused && (
                <div className="d-flex flex-wrap align-items-center gap-2 border-top pt-2 small">
                  {m.covered === false && <Badge bg="warning" text="dark">Not fully covered by the help</Badge>}
                  <span className="text-muted">Was this helpful?</span>
                  <Button size="sm" variant={m.helpful === true ? 'success' : 'outline-secondary'} aria-label="Helpful" onClick={() => rate(i, true)}>
                    <i className="bi bi-hand-thumbs-up" />
                  </Button>
                  <Button size="sm" variant={m.helpful === false ? 'danger' : 'outline-secondary'} aria-label="Not helpful" onClick={() => rate(i, false)}>
                    <i className="bi bi-hand-thumbs-down" />
                  </Button>
                  {(m.covered === false || m.helpful === false) && (
                    m.sent
                      ? <span className="text-success ms-auto">Sent to support</span>
                      : (
                        <Button size="sm" variant="outline-primary" className="ms-auto" disabled={m.sending} onClick={() => sendToSupport(i)}>
                          {m.sending ? 'Sending...' : 'Send this to support'}
                        </Button>
                      )
                  )}
                </div>
              )}
            </Card.Body>
          </Card>
        )
      ))}
      {busy && <div className="text-muted mb-3"><Spinner animation="border" size="sm" className="me-2" />Looking it up...</div>}
      <div ref={bottom} />

      <Form onSubmit={(e) => { e.preventDefault(); ask(); }}>
        <Form.Control
          as="textarea"
          ref={askBox}
          rows={2}
          value={question}
          maxLength={1000}
          disabled={busy}
          placeholder="Type your question"
          aria-label="Your question"
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask(); } }}
        />
        <div className="d-flex align-items-center gap-3 mt-2">
          <Button type="submit" disabled={busy || !question.trim() || left === 0}>Ask</Button>
          {messages.length > 0 && <Button variant="link" className="p-0" onClick={() => setMessages([])}>Start a new question</Button>}
          <span className="small text-muted ms-auto">{left} {left === 1 ? 'question' : 'questions'} left today</span>
        </div>
      </Form>
    </div>
  );
}

export default HelpAssistant;
